import * as React from "react"
import { FormControl, BoxProps, Box, useTheme } from "@chakra-ui/react"
import Select, { StylesConfig } from "react-select"

import { useFormContext, FieldError } from "react-hook-form"
import { InputError } from "./InputError"
import { InputLabel } from "./InputLabel"

export type MultiOption = { value: string; label: string }

interface Props extends BoxProps {
  name: string
  options: MultiOption[]
  parseValue?: boolean
  placeholder?: string
  label?: string
  subLabel?: string
  isRequired?: boolean
}

export const MultiSelect = ({ label, subLabel, isRequired, ...props }: Props) => {
  const theme = useTheme()

  const {
    formState: { errors },
    register,
    unregister,
    setValue,
    watch,
    clearErrors,
  } = useFormContext()
  const fieldError = errors?.[props.name] as FieldError | string

  React.useEffect(() => {
    register(props.name)
    return () => unregister(props.name)
  }, [register, unregister, props.name])

  const value = watch(props.name)

  const handleMultiChange = (val: any | null) => {
    const valueToSend = val && val.length > 0 ? val : null
    clearErrors(props.name)
    if (props.parseValue) {
      setValue(props.name, valueToSend ? JSON.stringify(valueToSend) : null)
    } else {
      setValue(props.name, valueToSend)
    }
  }
  let finalValue = value
  if (finalValue && props.parseValue) {
    finalValue = JSON.parse(finalValue)
  }
  return (
    <FormControl isInvalid={!!fieldError} isRequired={isRequired}>
      <InputLabel label={label} subLabel={subLabel} name={props.name} />
      <Box {...props}>
        <Select
          required={isRequired}
          value={finalValue}
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          onChange={handleMultiChange}
          isMulti
          placeholder={props.placeholder || ""}
          options={props.options}
          styles={customStyle(
            theme,
            !!fieldError,
            props.borderTopLeftRadius as number | string,
            props.borderBottomLeftRadius as number | string,
          )}
        />
      </Box>
      <InputError error={fieldError} />
    </FormControl>
  )
}

const customStyle = (
  theme: Record<string, any>,
  hasError: boolean,
  borderTopLeftRadius?: number | string,
  borderBottomLeftRadius?: number | string,
): StylesConfig<any, any> => ({
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 999,
  }),
  placeholder: (styles) => ({
    ...styles,
    overflow: "hidden",
    whiteSpace: "nowrap",
    color: theme.colors.gray[400],
  }),
  control: (styles, state) => ({
    ...styles,
    paddingTop: 1,
    paddingBottom: 1,
    paddingLeft: 6,
    borderWidth: 1,
    borderColor: state.isFocused ? theme.colors.blue[500] : theme.colors.gray[200],
    boxShadow: hasError
      ? `0 0 0 1px ${theme.colors.red[500]}`
      : state.isFocused
      ? `0 0 0 1px ${theme.colors.blue[500]}`
      : "transparent",
    borderTopLeftRadius: borderTopLeftRadius,
    borderBottomLeftRadius: borderBottomLeftRadius,
    ":hover": {
      borderColor: state.isFocused ? theme.colors.blue[500] : theme.colors.gray[300],
    },
  }),
})
